import React, {
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import { AnimatePresence } from '@sopranodesign/framer-motion';
import {
  Flex,
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  PanelSlider,
  TableActions,
  LoadingContainer,
  OrderableTableRow,
  usePaginationReducer,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import { useQuery } from '@sopranodesign/pulse-hooks';
import {
  useAuth,
  PermissionFilter
} from '@sopranodesign/pulse-auth';

import { StateLevel } from './';

const SitemapUrlExplore = ({
  sitemap: initialSitemap,
  handleHide,
  onOpenDeleteModal,
  onOpenRecrawlModal,
  onOpenTagsModal,
  loadingIds,
  errorIds
}) => {
  const { hasPermission } = useAuth();
  const canViewTag = hasPermission('view generative ai tag');
  const canEditTag = hasPermission('edit generative ai tag');
  const canEditResource = hasPermission('edit generative ai resource');

  const [shouldRefetch, setShouldRefetch] = useState(true);
  const [isVisible, setIsVisible] = useState(!!initialSitemap);

  const order = useOrderableTableReducer({ id: 'sitemapUrls' });
  const pagination = usePaginationReducer({ id: 'sitemapUrls' });

  useEffect(() => {
    setIsVisible(!!initialSitemap);
  }, [initialSitemap]);

  const {
    data: localSitemapUrls,
    error,
    isLoading
  } = useQuery({
    queryKey: [`generative-ai/sitemap/${initialSitemap?.id}/urls`, { ...pagination.params, ...order.params }],
    refetchInterval: isVisible && shouldRefetch ? 5000 : false,
    enabled: isVisible && !!initialSitemap
  });

  useEffect(() => {
    if (!isVisible) {
      setShouldRefetch(false);
      return;
    }
    const pending = localSitemapUrls?.has_pending;
    setShouldRefetch(pending);
  }, [isVisible, localSitemapUrls]);

  if (!isVisible) {
    return null;
  }

  const meta = localSitemapUrls?.meta ?? {};

  return (
    <AnimatePresence>
      <PanelSlider
        header="Explore Sitemap URLs"
        handleHide={() => {
          setIsVisible(false);
          handleHide();
        }}
      >
        <Flex col>
          {isLoading ? (
            <LoadingContainer />
          ) : error ? (
            <p style={{ color: 'red' }}>
              Failed to fetch sitemap URLs: {error.message || 'Unknown error occurred'}
            </p>
          ) : localSitemapUrls?.data?.length > 0 ? (
            <Flex border xScroll>
              <Table>
                <TableHead>
                  <OrderableTableRow
                    rows={[
                      { label: 'Url', sort: 'url' },
                      { label: 'Status', sort: 'status' },
                      { label: 'Crawled At', sort: 'updated_at' },
                      ...(canViewTag ? [{ label: 'Tags' }] : []),
                      ...(((canViewTag && canEditTag) || canEditResource) ? [null] : [])
                    ]}
                    {...order.props}
                  />
                </TableHead>
                <TableBody>
                  {localSitemapUrls.data.map(sitemap => (
                    <TableRow key={sitemap.id}>
                      <TableCell>
                        <a href={sitemap.url} target="_blank" rel="noopener noreferrer">
                          {sitemap.url}
                        </a>
                      </TableCell>
                      <TableCell>
                        <Flex center>
                          <StateLevel level={sitemap.status} />
                          <span>{sitemap.status}</span>
                        </Flex>
                      </TableCell>
                      <TableCell>{dayjs(sitemap.updated_at).format('D MMM YYYY HH:mm')}</TableCell>
                      <PermissionFilter can="view generative ai tag">
                        <TableCell>
                          {sitemap.groups && sitemap.groups.length > 0
                            ? sitemap.groups.map(group => group.name).join(', ')
                            : 'No Tags'}
                        </TableCell>
                      </PermissionFilter>
                      {((canViewTag && canEditTag) || canEditResource) &&
                        <TableActions>
                          <PermissionFilter can="view generative ai tag">
                            <PermissionFilter can="edit generative ai tag">
                              {sitemap.status === 'Success' && (
                                <Button
                                  icon="tag"
                                  onClick={() => onOpenTagsModal(sitemap)}
                                >
                                  Tags
                                </Button>
                              )}
                            </PermissionFilter>
                          </PermissionFilter>
                          <PermissionFilter can="edit generative ai resource">
                            {!['Manually Deleted', 'Deleting'].includes(sitemap.status) && (
                              <Button
                                colour="danger"
                                icon="trash"
                                loading={loadingIds[sitemap.id]}
                                onClick={() => onOpenDeleteModal(sitemap)}
                              >
                                Delete
                              </Button>
                            )}
                            {!['Deleting'].includes(sitemap.status) && (
                              <Button
                                icon="download"
                                loading={loadingIds[sitemap.id]}
                                onClick={() => onOpenRecrawlModal(sitemap)}
                              >
                                {sitemap.status !== 'Manually Deleted' ? 'Recrawl' : 'Enable and Crawl'}
                              </Button>
                            )}
                          </PermissionFilter>
                          {errorIds[sitemap.id] && (
                            <p style={{ color: 'red', fontSize: '12px' }}>{errorIds[sitemap.id]}</p>
                          )}
                        </TableActions>
                      }
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Flex>
          ) : (
            <p>No URLs available for this sitemap.</p>
          )}

          {meta && <Pagination pagination={meta} {...pagination.props} />}
        </Flex>
      </PanelSlider>
    </AnimatePresence >
  );
};

SitemapUrlExplore.propTypes = {
  handleHide: PropTypes.func.isRequired,
  sitemap: PropTypes.object.isRequired,
  onOpenDeleteModal: PropTypes.func.isRequired,
  onOpenRecrawlModal: PropTypes.func.isRequired,
  onOpenTagsModal: PropTypes.func.isRequired,
  loadingIds: PropTypes.object.isRequired,
  errorIds: PropTypes.object.isRequired
};

export default SitemapUrlExplore;